import { FileWarning } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { InlineError } from '@/components/ui/StatePanel';
import { useToast } from '@/components/ui/toast';
import {
  capabilityNote,
  isTypeRestricted,
  restrictedActionText,
  type CapabilityState,
} from '@/lib/document-capabilities';

import { useAddComment, useComments, useResolveComment } from '../use-comments';
import type { Comment, CommentStatus } from '../types';
import { formatDateTime } from '@/lib/time';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

interface Props {
  entryId: string;
  companyId: string | null;
  /** Comentar y resolver según el tipo del documento (spec 2026-10-14 §8 D6). */
  canComment: CapabilityState;
}

const STATUS_BADGE: Record<CommentStatus, { label: string; cls: string }> = {
  open: { label: 'Abierto', cls: 'b-warn' },
  addressed: { label: 'Atendido', cls: 'b-ok' },
  wont_fix: { label: 'No aplica', cls: 'b-mut' },
};

function CommentRow({
  c,
  onResolve,
}: {
  c: Comment;
  onResolve: ((id: string) => void) | null;
}): ReactElement {
  const badge = STATUS_BADGE[c.status];
  return (
    <div style={{ borderLeft: '2px solid var(--line-strong)', paddingLeft: 11 }}>
      <div className="rowb" style={{ gap: 8 }}>
        <span className={`badge ${badge.cls}`}>{badge.label}</span>
        <span className="dim-mono" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
          {formatDateTime(c.createdAt)}
        </span>
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 6 }}>{c.body}</p>
      {c.status === 'open' && onResolve !== null ? (
        <button
          type="button"
          className="btn mut sm"
          style={{ marginTop: 6 }}
          onClick={() => onResolve(c.id)}
        >
          Marcar resuelto
        </button>
      ) : null}
    </div>
  );
}

/** Hilo de comentarios real (control de cambios): listar, agregar, resolver. */
export function CommentsThread({ entryId, companyId, canComment }: Props): ReactElement {
  const enabled = companyId !== null;
  const query = useComments(entryId, { companyId, enabled });
  const add = useAddComment(entryId);
  const resolve = useResolveComment(entryId);
  const { showToast } = useToast();
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState(false);

  // El comentario es INMUTABLE (no se puede editar ni borrar): se confirma antes de
  // publicar para evitar dejar en el registro algo puesto por error.
  const requestSubmit = (): void => {
    if (draft.trim() === '' || add.isPending) {
      return;
    }
    setConfirming(true);
  };

  const confirmSubmit = (): void => {
    const body = draft.trim();
    if (body === '') {
      return;
    }
    add.mutate(
      { body },
      {
        onSuccess: () => {
          setDraft('');
          setConfirming(false);
          showToast('Comentario agregado');
        },
        onError: (err) =>
          showToast(
            isTypeRestricted(err)
              ? restrictedActionText('comentar')
              : 'No se pudo agregar el comentario',
            'error',
          ),
      },
    );
  };

  const onResolve = (id: string): void => {
    resolve.mutate(
      { commentId: id, status: 'addressed' },
      {
        onSuccess: () => showToast('Comentario marcado como atendido'),
        onError: (err) =>
          showToast(
            isTypeRestricted(err)
              ? restrictedActionText('comentar')
              : 'No se pudo resolver el comentario',
            'error',
          ),
      },
    );
  };

  const comments = query.data ?? [];
  // Una recarga que falla conserva lo que ya se veía (TanStack mantiene `data`).
  const failedEmpty = query.isError && query.data === undefined;
  return (
    <div className="card rise d2">
      <div className="ch">
        <h3>Comentarios</h3>
        {comments.length > 0 ? <span className="count">{comments.length}</span> : null}
      </div>
      {/* Un hilo que no cargó no se presenta como vacío (UX-ERR-01). */}
      {query.isError && (
        <InlineError
          icon={<FileWarning />}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        >
          {failedEmpty
            ? `No se pudieron cargar los comentarios. ${errorAdvice(query.error)}`
            : `No se pudieron actualizar los comentarios. Se muestran los anteriores.${refreshAdvice(query.error)}`}
        </InlineError>
      )}
      <div className="cb" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {failedEmpty ? null : query.isLoading ? (
          <p className="soft" style={{ fontSize: 12.5 }}>
            Cargando comentarios…
          </p>
        ) : comments.length === 0 ? (
          <p className="soft" style={{ fontSize: 12.5 }}>
            Sin comentarios todavía. Deja la primera observación.
          </p>
        ) : (
          comments.map((c) => (
            <CommentRow key={c.id} c={c} onResolve={canComment.allowed ? onResolve : null} />
          ))
        )}
        {!canComment.allowed ? (
          <p className="soft" style={{ fontSize: 12.5, margin: 0 }}>
            {capabilityNote(canComment, 'comentar')}
          </p>
        ) : (
          <div className="rowb" style={{ gap: 8 }}>
            <label htmlFor="comment-draft" className="sr-only">
              Escribir comentario
            </label>
            <input
              id="comment-draft"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Escribir comentario…"
              maxLength={5000}
              style={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  requestSubmit();
                }
              }}
            />
            <button
              type="button"
              className="btn sm"
              onClick={requestSubmit}
              disabled={add.isPending || draft.trim() === ''}
            >
              {add.isPending ? 'Enviando…' : 'Enviar'}
            </button>
          </div>
        )}
      </div>
      <ConfirmModal
        open={confirming}
        title="Publicar comentario"
        message="Una vez publicado, el comentario no se puede editar ni eliminar. ¿Publicarlo?"
        confirmLabel="Publicar comentario"
        busy={add.isPending}
        onConfirm={confirmSubmit}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
